import {
	type ImportRecordRule,
	ImportRecordRuleType,
	type ImportTransform,
	type ImportTransformDescriptor,
	type ImportTransformType,
	type MappingField,
	type RecordValidation,
} from '@today/types'
import { Button } from '@today/ui-primitives'
import { useState } from 'react'
import { TransformEditor } from './transform-editor.view'

const INPUT_CLASS =
	'h-9 rounded-ds-md border border-ds-border bg-ds-surface px-2 text-ds-sm text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring'

/** A canonical field the mapping can target. */
export interface MappingTarget {
	name: string
	type: string
	required: boolean
}

/**
 * Props for {@link FieldMappingEditor}.
 *
 * @property targets - The canonical schema's fields, in schema order.
 * @property sources - The source field names of the format.
 * @property mapping - The mapping items (at most one per target).
 * @property rules - The record validation rules, by canonical field.
 * @property catalogue - The transform catalogue.
 * @property onMappingChange - Called with the whole new mapping.
 * @property onRulesChange - Called with the whole new rule list.
 */
export interface FieldMappingEditorProps {
	targets: readonly MappingTarget[]
	sources: readonly string[]
	mapping: readonly MappingField[]
	rules: readonly RecordValidation[]
	catalogue: Readonly<Record<ImportTransformType, ImportTransformDescriptor>>
	onMappingChange: (mapping: MappingField[]) => void
	onRulesChange: (rules: RecordValidation[]) => void
}

/** A rule's value: `oneOf` takes comma-separated values, `min`/`max`/lengths a number, `pattern` text. */
const ruleOf = (type: ImportRecordRuleType, text: string): ImportRecordRule => {
	if (type === ImportRecordRuleType.REQUIRED) return { type }
	if (type === ImportRecordRuleType.ONE_OF) return { type, values: text.split(',').map((v) => v.trim()) }
	if (type === ImportRecordRuleType.PATTERN) return { type, value: text }
	return { type, value: Number(text) }
}

const ruleText = (rule: ImportRecordRule) =>
	rule.type === ImportRecordRuleType.REQUIRED
		? 'required'
		: `${rule.type} ${rule.values ? rule.values.join(', ') : String(rule.value)}`

function RuleAdder({ field, onAdd }: { field: string; onAdd: (rule: ImportRecordRule) => void }) {
	const [type, setType] = useState(ImportRecordRuleType.REQUIRED)
	const [value, setValue] = useState('')
	return (
		<div className="flex items-center gap-2">
			<select
				aria-label={`Rule to add to ${field}`}
				className={INPUT_CLASS}
				value={type}
				onChange={(e) => setType(e.target.value as ImportRecordRuleType)}
			>
				{Object.values(ImportRecordRuleType).map((t) => (
					<option key={t} value={t}>
						{t}
					</option>
				))}
			</select>
			{type !== ImportRecordRuleType.REQUIRED && (
				<input
					aria-label={`Rule value for ${field}`}
					className={INPUT_CLASS}
					value={value}
					onChange={(e) => setValue(e.target.value)}
				/>
			)}
			<Button
				variant="secondary"
				size="sm"
				aria-label={`Add rule to ${field}`}
				onPress={() => {
					onAdd(ruleOf(type, value))
					setValue('')
				}}
			>
				Add rule
			</Button>
		</div>
	)
}

function RuleEditor({
	field,
	rules,
	onChange,
}: {
	field: string
	rules: readonly ImportRecordRule[]
	onChange: (rules: ImportRecordRule[]) => void
}) {
	return (
		<div className="flex flex-col gap-1">
			<ul>
				{rules.map((rule, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: rules are ordered and may repeat
					<li key={i} className="flex items-center gap-2">
						<span>{ruleText(rule)}</span>
						<Button
							variant="muted"
							size="sm"
							aria-label={`Remove rule ${ruleText(rule)} from ${field}`}
							onPress={() => onChange(rules.filter((_, j) => j !== i))}
						>
							Remove
						</Button>
					</li>
				))}
			</ul>
			<RuleAdder field={field} onAdd={(rule) => onChange([...rules, rule])} />
		</div>
	)
}

interface MappingRowProps {
	target: MappingTarget
	sources: readonly string[]
	item: MappingField | undefined
	rules: readonly ImportRecordRule[]
	catalogue: FieldMappingEditorProps['catalogue']
	onSource: (source: string) => void
	onTransforms: (transforms: ImportTransform[]) => void
	onRules: (rules: ImportRecordRule[]) => void
}

function MappingRow({ target, sources, item, rules, catalogue, onSource, onTransforms, onRules }: MappingRowProps) {
	return (
		<li className="flex flex-col gap-2 rounded-ds-control border border-ds-border p-ds-3">
			<div className="flex items-center gap-2">
				<span className="font-ds-heading">
					{target.name}
					{target.required ? ' (required)' : ''}
				</span>
				<span>{target.type}</span>
				<select
					aria-label={`Source for ${target.name}`}
					className={INPUT_CLASS}
					value={item?.sources[0] ?? ''}
					onChange={(e) => onSource(e.target.value)}
				>
					<option value="">Not mapped</option>
					{sources.map((source) => (
						<option key={source} value={source}>
							{source}
						</option>
					))}
				</select>
			</div>
			{item && (
				<>
					<TransformEditor
						label={target.name}
						transforms={item.transforms}
						catalogue={catalogue}
						onChange={onTransforms}
					/>
					<RuleEditor field={target.name} rules={rules} onChange={onRules} />
				</>
			)}
		</li>
	)
}

/**
 * Source → canonical mapping (TO-400 SCN-008): per canonical field, the source
 * it reads, its transforms and its record rules. A required field is marked;
 * F5 refuses a contract that leaves one unmapped. Pure.
 */
export function FieldMappingEditor({
	targets,
	sources,
	mapping,
	rules,
	catalogue,
	onMappingChange,
	onRulesChange,
}: FieldMappingEditorProps) {
	const itemOf = (target: string) => mapping.find((m) => m.target === target)
	const setSource = (target: string, source: string) => {
		const rest = mapping.filter((m) => m.target !== target)
		if (source === '') return onMappingChange(rest)
		const current = itemOf(target)
		const item: MappingField = { sources: [source], target, transforms: current?.transforms ?? [] }
		onMappingChange(current ? mapping.map((m) => (m.target === target ? item : m)) : [...mapping, item])
	}
	const setRules = (field: string, fieldRules: ImportRecordRule[]) => {
		const rest = rules.filter((r) => r.field !== field)
		onRulesChange(fieldRules.length === 0 ? rest : [...rest, { field, rules: fieldRules }])
	}
	return (
		<ul className="flex flex-col gap-4 text-ds-sm text-ds-text">
			{targets.map((target) => (
				<MappingRow
					key={target.name}
					target={target}
					sources={sources}
					item={itemOf(target.name)}
					rules={rules.find((r) => r.field === target.name)?.rules ?? []}
					catalogue={catalogue}
					onSource={(source) => setSource(target.name, source)}
					onTransforms={(transforms) =>
						onMappingChange(mapping.map((m) => (m.target === target.name ? { ...m, transforms } : m)))
					}
					onRules={(fieldRules) => setRules(target.name, fieldRules)}
				/>
			))}
		</ul>
	)
}
